/**
 * Saved-views control + tile-loading indicator.
 *
 * Floating control in the bottom-right corner of the map that lets the
 * operator capture the current camera (lon, lat, zoom, bearing, pitch)
 * as a named "view" and fly back to it later.
 *
 * WP-1 update: the legacy `window.prompt('Name this view', ...)` flow is
 * **removed**. The "Save view" button now opens a tokenized
 * `SaveScenePresetDialog` (Material 3 `Dialog` primitive), captures the
 * current visibility + opacities + camera + time, and persists to the
 * gTwin via `gtwinClient.scene.presets.upsert(...)` followed by
 * `activate(...)`. The dialog also writes a local cache entry so the
 * popover continues to fly back without a round-trip — the cache is a
 * convenience, the gTwin scene preset is the durable record.
 *
 * Token-only styling. No `window.prompt`. No emoji. Material Symbols
 * Rounded only via the `@/design` `Icon` primitive.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getMapLibreHostHandle } from '@/engines/maplibre/hostHandle';
import { Icon, tokens } from '@/design';
import { gtwinClient } from '@/app/gtwinClientSingleton';
import { useViewerStore } from '@/state/useViewerStore';
import { SaveScenePresetDialog } from '@/components/gtwin/SaveScenePresetDialog';
import type {
  ScenePresetInput,
  SceneSlot,
} from '@/sdk-stub/gtwin-sdk';

interface SavedView {
  name: string;
  presetId: string;
  lon: number;
  lat: number;
  zoom: number;
  bearing?: number;
  pitch?: number;
  createdAt: string;
}

const STORAGE_PREFIX = 'geoviewer.savedViews';

function loadSavedViews(scope: string): SavedView[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}.${scope}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as SavedView[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveSavedViews(scope: string, views: SavedView[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}.${scope}`, JSON.stringify(views));
  } catch {
    /* ignore */
  }
}

export interface SavedViewsControlProps {
  container: HTMLElement | null;
  scope: string;
  inline?: boolean;
}

const buttonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: tokens.space[2],
  padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
  background: tokens.color.surface[1],
  color: tokens.color.ink.primary,
  border: tokens.border.thin,
  borderRadius: tokens.radius.input,
  cursor: 'pointer',
  fontSize: tokens.font.size.sm,
  fontFamily: tokens.font.family.ui,
  boxShadow: tokens.elevation[2],
  minHeight: tokens.density.touchMin,
};

export function SavedViewsControl({ container, scope, inline = false }: SavedViewsControlProps) {
  const [views, setViews] = useState<SavedView[]>(() => loadSavedViews(scope));
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialogState, setDialogState] = useState<
    | null
    | {
        readonly visibleLayerIds: ReadonlyArray<string>;
        readonly visibleSlots: ReadonlyArray<SceneSlot>;
        readonly camera: ScenePresetInput['camera'];
        readonly localView: Omit<SavedView, 'presetId'>;
      }
  >(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const visibleLayerIds = useViewerStore((s) => s.visibleLayerIds);
  const slotMap = useViewerStore((s) => s.slotMap);

  // Refresh saved views if the project (scope) changes.
  useEffect(() => {
    setViews(loadSavedViews(scope));
  }, [scope]);

  // Click-outside closes the popover.
  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current && !popoverRef.current.contains(target)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  // Wire MapLibre data-loading events to drive the loading pill.
  useEffect(() => {
    if (!container) return;
    const map = getMapLibreHostHandle(container);
    if (!map) return;
    let pending = 0;
    const onStart = () => {
      pending += 1;
      setLoading(true);
    };
    const onEnd = () => {
      pending = Math.max(0, pending - 1);
      if (pending === 0) setLoading(false);
    };
    map.on('dataloading', onStart);
    map.on('data', onEnd);
    map.on('idle', () => {
      pending = 0;
      setLoading(false);
    });
    return () => {
      try {
        map.off('dataloading', onStart);
        map.off('data', onEnd);
      } catch {
        /* map may be torn down */
      }
    };
  }, [container]);

  const captureForDialog = useCallback(() => {
    if (!container) return;
    const map = getMapLibreHostHandle(container);
    if (!map) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    const bearing = map.getBearing();
    const pitch = map.getPitch();
    const slotsInUse = new Set<SceneSlot>();
    for (const id of visibleLayerIds) {
      const slot = slotMap[id];
      if (slot) slotsInUse.add(slot);
    }
    setDialogState({
      visibleLayerIds: Array.from(visibleLayerIds),
      visibleSlots: Array.from(slotsInUse),
      camera: {
        lonLat: [center.lng, center.lat],
        zoom,
        bearing,
        pitch,
      },
      localView: {
        name: '',
        lon: center.lng,
        lat: center.lat,
        zoom,
        bearing,
        pitch,
        createdAt: new Date().toISOString(),
      },
    });
  }, [container, visibleLayerIds, slotMap]);

  const flyTo = (view: SavedView) => {
    if (!container) return;
    const map = getMapLibreHostHandle(container);
    if (!map) return;
    map.flyTo({
      center: [view.lon, view.lat],
      zoom: view.zoom,
      bearing: view.bearing,
      pitch: view.pitch,
      duration: tokens.motion.duration.slow,
    });
    setOpen(false);
  };

  const removeView = (idx: number) => {
    const updated = views.filter((_, i) => i !== idx);
    setViews(updated);
    saveSavedViews(scope, updated);
  };

  return (
    <div
      style={{
        position: inline ? 'relative' : 'absolute',
        right: inline ? undefined : tokens.space[4],
        bottom: inline ? undefined : tokens.layout.bottomBarHeight + tokens.space[4],
        zIndex: tokens.zIndex.panel,
        display: 'flex',
        flexDirection: 'column',
        alignItems: inline ? 'stretch' : 'flex-end',
        gap: tokens.space[2],
        fontFamily: tokens.font.family.ui,
      }}
    >
      {loading ? (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
            padding: `${tokens.space[1]}px ${tokens.space[3]}px`,
            background: tokens.color.surface[1],
            color: tokens.color.ink.primary,
            border: tokens.border.thin,
            borderRadius: tokens.radius.pill,
            fontSize: tokens.font.size.xs,
            boxShadow: tokens.elevation[1],
          }}
        >
          <Icon name="refresh" size={16} decorative />
          <span>Loading tiles…</span>
        </div>
      ) : null}

      <div style={{ display: 'flex', gap: tokens.space[2] }}>
        <button
          type="button"
          onClick={captureForDialog}
          title="Save the current camera and visibility as a scene preset on this gTwin"
          style={buttonStyle}
          data-testid="saved-views-save"
        >
          <Icon name="bookmark" size={20} decorative />
          <span>Save scene preset</span>
        </button>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          title="Saved camera views (local cache)"
          style={buttonStyle}
          data-testid="saved-views-toggle"
        >
          <Icon name="visibility" size={20} decorative />
          <span>Views ({views.length})</span>
        </button>
      </div>

      {open ? (
        <div
          ref={popoverRef}
          role="listbox"
          aria-label="Saved views"
          style={{
            minWidth: tokens.layout.popoverMd,
            background: tokens.color.surface[1],
            color: tokens.color.ink.primary,
            border: tokens.border.thin,
            borderRadius: tokens.radius.md,
            boxShadow: tokens.elevation[3],
            padding: tokens.space[2],
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.space[1],
          }}
        >
          {views.length === 0 ? (
            <span
              style={{
                color: tokens.color.ink.secondary,
                fontSize: tokens.font.size.sm,
                padding: tokens.space[2],
              }}
            >
              No saved views yet. Press “Save scene preset”.
            </span>
          ) : (
            views.map((v, idx) => (
              <div
                key={`${v.presetId}-${idx}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: tokens.space[2],
                  padding: tokens.space[2],
                  borderRadius: tokens.radius.input,
                }}
              >
                <button
                  type="button"
                  role="option"
                  aria-selected="false"
                  onClick={() => flyTo(v)}
                  style={{
                    flex: 1,
                    textAlign: 'left',
                    padding: tokens.space[1],
                    background: 'transparent',
                    color: tokens.color.ink.primary,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: tokens.font.size.sm,
                    fontFamily: tokens.font.family.ui,
                    minHeight: tokens.density.touchMin,
                  }}
                >
                  <div style={{ fontWeight: tokens.font.weight.medium }}>{v.name}</div>
                  <div
                    style={{
                      fontSize: tokens.font.size.xs,
                      color: tokens.color.ink.secondary,
                      fontFamily: tokens.font.family.mono,
                    }}
                  >
                    {v.lon.toFixed(4)}, {v.lat.toFixed(4)} · z{v.zoom.toFixed(1)}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => removeView(idx)}
                  aria-label={`Delete saved view ${v.name}`}
                  title="Delete this view"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: tokens.space[1],
                    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
                    background: 'transparent',
                    color: tokens.color.semantic.danger,
                    border: tokens.border.thin,
                    borderRadius: tokens.radius.input,
                    cursor: 'pointer',
                    fontSize: tokens.font.size.xs,
                    minHeight: tokens.density.touchMin,
                  }}
                >
                  <Icon name="delete" size={16} decorative />
                  Remove
                </button>
              </div>
            ))
          )}
        </div>
      ) : null}

      {dialogState ? (
        <SaveScenePresetDialog
          gtwinId={scope}
          capture={{
            visibleLayerIds: dialogState.visibleLayerIds,
            visibleSlots: dialogState.visibleSlots,
            camera: dialogState.camera,
          }}
          adapter={gtwinClient.scene.presets}
          existingPresetIds={views.map((v) => v.presetId)}
          optimistic={{
            apply: (preset) => {
              const cached: SavedView = {
                ...dialogState.localView,
                name: preset.label,
                presetId: preset.id,
              };
              const next = [cached, ...views.filter((v) => v.presetId !== preset.id)];
              setViews(next);
              saveSavedViews(scope, next);
            },
            rollback: () => {
              setViews(loadSavedViews(scope));
            },
          }}
          onSaved={(preset) => {
            const refreshed = loadSavedViews(scope);
            const cached: SavedView = {
              ...dialogState.localView,
              name: preset.label,
              presetId: preset.id,
            };
            const next = [cached, ...refreshed.filter((v) => v.presetId !== preset.id)];
            setViews(next);
            saveSavedViews(scope, next);
            setDialogState(null);
          }}
          onClose={() => setDialogState(null)}
        />
      ) : null}
    </div>
  );
}
